import React, { useState, useEffect } from "react";
import {
  Layout,
  Space,
  Table,
  Card,
  Tag,
  Button,
  Row,
  Col,
  Statistic,
  Typography,
  Input,
  Select,
  DatePicker,
  message,
  Modal,
  Descriptions,
  Alert,
  Tooltip,
  Badge,
} from "antd";
import {
  BankOutlined,
  CheckCircleOutlined,
  ClockCircleOutlined,
  DollarOutlined,
  ExclamationCircleOutlined,
  ReloadOutlined,
  SearchOutlined,
  SyncOutlined,
  UserOutlined,
  MailOutlined,
} from "@ant-design/icons";
import { getApi, postApi, moneyFormat, isSuperAdmin } from "@utils";
import { Header } from "@components";
import moment from "moment";

const { Content } = Layout;
const { Title, Text } = Typography;
const { RangePicker } = DatePicker;

export const AdminStripeTransfers = () => {
  const [loading, setLoading] = useState(false);
  const [transfers, setTransfers] = useState([]);
  const [stats, setStats] = useState({});
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 25,
    total: 0,
  });
  const [filters, setFilters] = useState({
    status: null,
    dateRange: null,
  });

  useEffect(() => {
    if (!isSuperAdmin()) {
      window.location.href = "/";
      return;
    }
    fetchTransfers();
  }, [pagination.current, filters]);

  const fetchTransfers = async () => {
    setLoading(true);
    try {
      const params = {
        page: pagination.current,
        per_page: pagination.pageSize,
        status: filters.status,
      };

      if (filters.dateRange && filters.dateRange.length === 2) {
        params.date_from = filters.dateRange[0].format("YYYY-MM-DD");
        params.date_to = filters.dateRange[1].format("YYYY-MM-DD");
      }

      const response = await getApi("getAdminStripeTransfers", params);

      if (response?.data) {
        setTransfers(response.data);
        setStats(response.stats || {});
        setPagination((prev) => ({
          ...prev,
          total: response.count,
        }));
      }
    } catch (error) {
      message.error("Failed to load transfers");
    }
    setLoading(false);
  };

  const handleBulkTransfer = async () => {
    Modal.confirm({
      title: "Process Bulk Transfers",
      content: "This will process all confirmed payouts. Are you sure?",
      onOk: async () => {
        try {
          const response = await postApi("postAdminStripeTransfersBulk", {});
          message.success(
            `Processed: ${response.data.processed}, Failed: ${response.data.failed}, Skipped: ${response.data.skipped}`
          );
          fetchTransfers();
        } catch (error) {
          message.error("Failed to process bulk transfers");
        }
      },
    });
  };

  const handleRetry = async (transferId) => {
    try {
      await postApi("postAdminTransferRetry", {}, { id: transferId });
      message.success("Transfer retry initiated");
      fetchTransfers();
    } catch (error) {
      message.error("Failed to retry transfer");
    }
  };

  const handleCancel = async (transferId) => {
    Modal.confirm({
      title: "Cancel Transfer",
      content: "Are you sure you want to cancel this transfer?",
      onOk: async () => {
        try {
          await postApi("postAdminTransferCancel", {}, { id: transferId });
          message.success("Transfer canceled");
          fetchTransfers();
        } catch (error) {
          message.error("Failed to cancel transfer");
        }
      },
    });
  };

  const getStatusTag = (status) => {
    const statusConfig = {
      pending: {
        color: "orange",
        icon: <ClockCircleOutlined />,
        text: "Pending",
      },
      in_transit: {
        color: "processing",
        icon: <SyncOutlined spin />,
        text: "In Transit",
      },
      paid: { color: "green", icon: <CheckCircleOutlined />, text: "Paid" },
      failed: {
        color: "red",
        icon: <ExclamationCircleOutlined />,
        text: "Failed",
      },
      canceled: {
        color: "default",
        icon: <ExclamationCircleOutlined />,
        text: "Canceled",
      },
      reversed: {
        color: "warning",
        icon: <ExclamationCircleOutlined />,
        text: "Reversed",
      },
      completed: {
        color: "green",
        icon: <CheckCircleOutlined />,
        text: "Completed",
      },
    };
    const config = statusConfig[status] || statusConfig.pending;
    return (
      <Tag color={config.color} icon={config.icon}>
        {config.text}
      </Tag>
    );
  };

  const columns = [
    {
      title: "ID",
      dataIndex: "id",
      width: 80,
    },
    {
      title: "User",
      dataIndex: "stripe_account",
      render: (account) => (
        <div>
          <Text strong>{account?.user_email || "N/A"}</Text>
        </div>
      ),
    },
    {
      title: "Amount",
      dataIndex: "amount",
      render: (amount) => <Text strong>{moneyFormat(amount || 0)}</Text>,
    },
    {
      title: "Status",
      dataIndex: "status",
      render: (status) => getStatusTag(status),
    },
    {
      title: "Created",
      dataIndex: "created_at",
      render: (date) =>
        date ? moment(date).format("MMM DD, YYYY HH:mm") : "-",
    },
    {
      title: "Available",
      dataIndex: "available_on",
      render: (date) => (date ? moment(date).format("MMM DD, YYYY") : "-"),
    },
    {
      title: "Failure",
      dataIndex: "failure_message",
      render: (message) =>
        message ? (
          <Tooltip title={message}>
            <Text type="danger">View Error</Text>
          </Tooltip>
        ) : (
          "-"
        ),
    },
    {
      title: "Actions",
      render: (_, record) => (
        <Space>
          {record.status === "failed" && (
            <Button
              size="small"
              type="primary"
              onClick={() => handleRetry(record.id)}
            >
              Retry
            </Button>
          )}
          {record.status === "pending" && (
            <Button size="small" danger onClick={() => handleCancel(record.id)}>
              Cancel
            </Button>
          )}
        </Space>
      ),
    },
  ];

  return (
    <Space
      direction="vertical"
      style={{ width: "100%", backgroundColor: "white" }}
    >
      <Header selected="admin_stripe_transfers" />
      <Content style={{ padding: "24px" }}>
        <Row
          justify="space-between"
          align="middle"
          style={{ marginBottom: 24 }}
        >
          <Title level={4}>Stripe Transfers Management</Title>
        </Row>

        <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
          <Col xs={24} sm={12} md={6}>
            <Card>
              <Statistic
                title="Pending"
                value={stats.pending_count || 0}
                suffix={`/ ${moneyFormat(stats.pending_amount || 0)}`}
                valueStyle={{ color: "#faad14" }}
              />
            </Card>
          </Col>
          <Col xs={24} sm={12} md={6}>
            <Card>
              <Statistic
                title="Completed"
                value={stats.completed_count || 0}
                suffix={`/ ${moneyFormat(stats.completed_amount || 0)}`}
                valueStyle={{ color: "#3f8600" }}
              />
            </Card>
          </Col>
          <Col xs={24} sm={12} md={6}>
            <Card>
              <Statistic
                title="Failed"
                value={stats.failed_count || 0}
                suffix={`/ ${moneyFormat(stats.failed_amount || 0)}`}
                valueStyle={{ color: "#cf1322" }}
              />
            </Card>
          </Col>
          <Col xs={24} sm={12} md={6}>
            <Card>
              <Statistic
                title="Total Volume"
                value={stats.completed_amount || 0}
                prefix="$"
                precision={2}
              />
            </Card>
          </Col>
        </Row>

        <Card>
          <Space style={{ marginBottom: 16 }}>
            <Select
              placeholder="Filter by status"
              allowClear
              value={filters.status}
              onChange={(value) => setFilters({ ...filters, status: value })}
              style={{ width: 150 }}
            >
              <Select.Option value="pending">Pending</Select.Option>
              <Select.Option value="in_transit">In Transit</Select.Option>
              <Select.Option value="paid">Paid</Select.Option>
              <Select.Option value="failed">Failed</Select.Option>
              <Select.Option value="canceled">Canceled</Select.Option>
              <Select.Option value="reversed">Reversed</Select.Option>
              <Select.Option value="completed">Completed</Select.Option>
            </Select>
            <RangePicker
              value={filters.dateRange}
              onChange={(dates) => setFilters({ ...filters, dateRange: dates })}
            />
            <Button onClick={() => fetchTransfers()} icon={<ReloadOutlined />}>
              Refresh
            </Button>
          </Space>

          <Table
            columns={columns}
            dataSource={transfers}
            rowKey="id"
            loading={loading}
            pagination={{
              ...pagination,
              onChange: (page, pageSize) => {
                setPagination({ ...pagination, current: page, pageSize });
              },
            }}
          />
        </Card>
      </Content>
    </Space>
  );
};
